// A folder's contents at /o/:slug/folders/:fid (F2 / FR-L1): the same card grid as the
// library, scoped to one folder. Folders organize; they don't grant access — this page can
// only ever show recordings the viewer could already see (server/src/folders.rs's
// grackles_in_folder, filtered through authz::can_view_sql before any pagination).
//
// Rename/delete are offered to any viewer who can *see* the folder; whether they can *manage*
// it (team folders need a team admin, org folders an org admin) is the server's call
// (`can_manage_folder`), and a denial comes back as the same 404 "not found" every other
// denial in this app does (§4.10) — shown here as a plain message rather than a dead end.
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button as DsButton, Callout } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { FolderOut, GrackleDetail, GrackleOut } from "../api/types";
import { AppShell } from "../components/AppShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { PageLoader, EmptyState } from "../components/ui";
import { useSession } from "../session";
import { deleteFolder, folderGrackles, listFolders } from "./api";
import FolderFormDialog from "./FolderFormDialog";
import { cardPageStyle } from "./cardColumns";
import GrackleCard from "./GrackleCard";
import "./library.css";

export default function FolderPage() {
  const { slug, fid } = useParams();
  const navigate = useNavigate();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [folder, setFolder] = useState<FolderOut | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [items, setItems] = useState<GrackleOut[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newSubfolderOpen, setNewSubfolderOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (orgId: string, folderId: string) => {
    setLoading(true);
    setError(null);
    setFolderError(null);
    try {
      // The folders API has no "get one" route — list and find, same as the sidebar does.
      const [folders, page] = await Promise.all([
        listFolders(orgId),
        folderGrackles(orgId, folderId, { limit: 24 }),
      ]);
      const found = folders.items.find((f) => f.id === folderId) ?? null;
      if (!found) {
        setFolderError("You don't have access to this folder, or it no longer exists.");
      }
      setFolder(found);
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load this folder.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!org || !fid) return;
    void load(org.id, fid);
  }, [org?.id, fid, load]);

  async function loadMore() {
    if (!org || !fid || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await folderGrackles(org.id, fid, { limit: 24, cursor: nextCursor });
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  const onThumbnailStale = useCallback((grackleId: string) => {
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((detail) => {
        setItems((prev) => prev.map((g) => (g.id === grackleId ? { ...g, thumbnail_url: detail.thumbnail_url } : g)));
      })
      .catch(() => {
        // Best-effort — leave the placeholder showing if the refetch also fails.
      });
  }, []);

  const onDeletedItem = useCallback((grackleId: string) => {
    setItems((prev) => prev.filter((g) => g.id !== grackleId));
  }, []);

  const onMovedItem = useCallback((grackleId: string, movedToFolderId: string | null) => {
    // Moved away from this folder (or unfiled): it no longer belongs in this listing.
    if (movedToFolderId !== fid) {
      setItems((prev) => prev.filter((g) => g.id !== grackleId));
    }
  }, [fid]);

  async function onDeleteFolder() {
    if (!org || !folder) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteFolder(org.id, folder.id);
      setDeleteOpen(false);
      navigate(-1);
    } catch (err) {
      setDeleteError(err instanceof ApiFail ? err.message : "Couldn't delete this folder.");
    } finally {
      setDeleting(false);
    }
  }

  const canRecord = user?.email_verified ?? false;

  if (sessionLoading) return <PageLoader label="Loading folder…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  return (
    <AppShell org={org} user={user} section={fid ?? "library"} canRecord={canRecord} fullWidth>
      <div className="lib-page" style={cardPageStyle(loading ? 8 : items.length, !!nextCursor)}>
        <div className="lib-header">
          <div className="lib-header-copy">
            <h1 className="lib-header-title">{folder?.name ?? "Folder"}</h1>
            <div className="lib-header-sub">
              {folderError ?? (loading ? "Loading…" : `${items.length} recording${items.length === 1 ? "" : "s"}`)}
            </div>
          </div>
          {folder ? (
            <div className="lib-header__actions">
              <DsButton variant="secondary" size="sm" icon="plus" onClick={() => setNewSubfolderOpen(true)}>
                Subfolder
              </DsButton>
              <DsButton variant="secondary" size="sm" onClick={() => setRenameOpen(true)}>
                Rename
              </DsButton>
              <DsButton variant="ghost" size="sm" onClick={() => setDeleteOpen(true)}>
                Delete folder
              </DsButton>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="lib-error">
            <Callout tone="danger" icon="warning-circle">
              {error}
            </Callout>
          </div>
        ) : null}

        {error ? null : loading ? (
          <PageLoader label="Loading folder…" />
        ) : items.length === 0 ? (
          <EmptyState
            className="lib-empty"
            icon="folder-simple"
            title="Nothing in this folder yet"
            body="Move a recording here from its card menu, or from the library."
          />
        ) : (
          <>
            <div className="lib-grid">
              {items.map((g) => (
                <GrackleCard key={g.id} grackle={g} onThumbnailStale={onThumbnailStale} onDeleted={onDeletedItem} onMoved={onMovedItem} />
              ))}
            </div>
            {nextCursor ? (
              <div className="lib-load-more">
                <DsButton variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </DsButton>
              </div>
            ) : null}
          </>
        )}
      </div>

      {renameOpen && folder ? (
        <FolderFormDialog
          mode="rename"
          org={org}
          folder={folder}
          onClose={() => setRenameOpen(false)}
          onSaved={(updated) => {
            setFolder(updated);
            setRenameOpen(false);
          }}
        />
      ) : null}

      {newSubfolderOpen && folder ? (
        <FolderFormDialog
          mode="create"
          org={org}
          parent={folder}
          onClose={() => setNewSubfolderOpen(false)}
          onSaved={() => setNewSubfolderOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete folder"
        message={
          <>
            {`Delete "${folder?.name ?? ""}"? Recordings inside become unfiled, and any subfolders are deleted too.`}
            {deleteError ? <Callout tone="danger" icon="warning-circle">{deleteError}</Callout> : null}
          </>
        }
        confirmLabel="Delete folder"
        danger
        loading={deleting}
        onClose={() => {
          setDeleteOpen(false);
          setDeleting(false);
        }}
        onConfirm={onDeleteFolder}
      />
    </AppShell>
  );
}
